import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Mic, MicOff, Square, Play, Pause, Trash2, Send } from 'lucide-react';
import { useVoiceRecording } from '../../hooks/useVoiceRecording';
import type { VoiceRecorderProps, VoiceMessageConfig } from '../../types/voice';

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onTranscriptionComplete,
  onError,
  onCancel,
  disabled = false,
  config,
  className = '',
  autoStart = false
}) => {
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const wasRecordingRef = useRef(false); // Track if we were actually recording

  const voiceConfig: Partial<VoiceMessageConfig> = useMemo(() => ({
    maxRecordingDuration: 60,
    minRecordingDuration: 1,
    audioFormat: 'audio/webm;codecs=opus',
    sampleRate: 44100,
    ...config
  }), [config]);

  const {
    recordingState,
    startRecording,
    stopRecording,
    transcribeAudio,
    checkMicrophonePermission,
    requestMicrophoneAccess,
    isSupported,
    getRecordingBlob,
    clearError,
    resetRecording
  } = useVoiceRecording(voiceConfig);

  // Request microphone permission immediately when component mounts
  useEffect(() => {
    const initializeMicrophone = async () => {
      if (!isSupported) return;

      try {
        const hasPermission = await checkMicrophonePermission();
        if (!hasPermission) {
          console.log('Microphone permission not granted, will request when needed');
        }
      } catch (error) {
        console.warn('Failed to check microphone permission:', error);
      }
    };

    initializeMicrophone();
  }, [isSupported, checkMicrophonePermission]);

  // Track when recording starts
  useEffect(() => {
    if (recordingState.isRecording) {
      wasRecordingRef.current = true;
    }
  }, [recordingState.isRecording]);

  // Auto-start recording when requested
  useEffect(() => {
    const maybeAutoStart = async () => {
      if (
        autoStart &&
        !disabled &&
        isSupported &&
        !recordingState.isRecording &&
        !recordingState.isProcessing &&
        !audioBlob
      ) {
        try {
          await handleStartRecording();
        } catch (error) {
          onError?.(error as Error);
        }
      }
    };

    maybeAutoStart();
  }, [autoStart, disabled, isSupported]);

  // Handle recording completion and auto-transcription
  useEffect(() => {
    const handleRecordingComplete = async () => {
      // Only try to get blob if we were actually recording and now stopped
      console.log('Recording complete, isRecording:', recordingState.isRecording, 'audioBlob:', audioBlob, 'wasRecordingRef.current:', wasRecordingRef.current);
      if (!recordingState.isRecording && !audioBlob && wasRecordingRef.current) {
        try {
          const blob = await getRecordingBlob();
          if (blob) {
            const url = URL.createObjectURL(blob);
            setAudioBlob(blob);
            setAudioUrl(url);
            console.log('Recording blob:', blob, url);

            // Auto-transcribe immediately
            try {
              const response = await transcribeAudio(blob);
              onTranscriptionComplete?.(response);
              handleCancelRecording(); // Clean up after successful transcription
            } catch (transcriptionError) {
              console.error('Auto-transcription failed:', transcriptionError);
              // Keep the recording for manual transcription
            }
          }
        } catch (error) {
          console.error('Failed to get recording blob:', error);
        } finally {
          wasRecordingRef.current = false; // Reset the flag
        }
      }
    };

    handleRecordingComplete();
  }, [recordingState.isRecording, audioBlob, getRecordingBlob, transcribeAudio, onTranscriptionComplete]);

  // Handle errors
  useEffect(() => {
    if (recordingState.error) {
      onError?.(new Error(recordingState.error));
    }
  }, [recordingState.error, onError]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, [audioUrl, audioElement]);

  // Format recording duration
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle start recording
  const handleStartRecording = async () => {
    try {
      clearError();

      // Reset previous recording
      if (audioBlob) {
        handleCancelRecording();
      }

      // Start recording will handle permission request internally
      await startRecording();
    } catch (error) {
      onError?.(error as Error);
    }
  };

  // Handle stop recording
  const handleStopRecording = () => {
    try {
      stopRecording();
    } catch (error) {
      onError?.(error as Error);
    }
  };

  // Handle cancel recording
  const handleCancelRecording = () => {
    try {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
        setAudioElement(null);
      }
      setAudioBlob(null);
      setIsPlaying(false);
      resetRecording();
      onCancel?.();
    } catch (error) {
      onError?.(error as Error);
    }
  };

  // Handle play/pause audio
  const handlePlayPause = () => {
    if (!audioUrl) return;

    try {
      if (!audioElement) {
        const audio = new Audio(audioUrl);
        audio.addEventListener('ended', () => setIsPlaying(false));
        audio.addEventListener('error', (e) => onError?.(new Error('Audio playback error')));
        setAudioElement(audio);
        audio.play();
        setIsPlaying(true);
      } else {
        if (isPlaying) {
          audioElement.pause();
          setIsPlaying(false);
        } else {
          audioElement.play();
          setIsPlaying(true);
        }
      }
    } catch (error) {
      onError?.(error as Error);
    }
  };

  // Handle send recording for transcription
  const handleSendRecording = async () => {
    if (!audioBlob) return;

    try {
      const response = await transcribeAudio(audioBlob);
      onTranscriptionComplete?.(response);
      handleCancelRecording(); // Clean up after successful transcription
    } catch (error) {
      onError?.(error as Error);
    }
  };

  // Handle permission request
  const handleRequestPermission = async () => {
    try {
      clearError();
      await requestMicrophoneAccess();
      // After granting permission, try to start recording
      if (recordingState.hasPermission) {
        await startRecording();
      }
    } catch (error: any) {
      // Handle specific permission errors
      if (error.name === 'NotAllowedError') {
        onError?.(new Error('Cần cấp quyền truy cập microphone để sử dụng tính năng ghi âm. Vui lòng cho phép truy cập microphone trong cài đặt trình duyệt.'));
      } else if (error.name === 'NotFoundError') {
        onError?.(new Error('Không tìm thấy microphone trên thiết bị. Vui lòng kiểm tra kết nối microphone.'));
      } else if (error.name === 'NotSupportedError') {
        onError?.(new Error('Trình duyệt không hỗ trợ ghi âm. Vui lòng sử dụng trình duyệt khác.'));
      } else if (error.name === 'NotReadableError') {
        onError?.(new Error('Microphone đang được sử dụng bởi ứng dụng khác. Vui lòng đóng các ứng dụng khác và thử lại.'));
      } else {
        onError?.(new Error(`Lỗi truy cập microphone: ${error.message}`));
      }
    }
  };

  // Get audio level for visualization
  const getAudioLevelPercentage = (): number => {
    return Math.min(recordingState.audioLevel * 100, 100);
  };

  // Show not supported message
  if (!isSupported) {
    return (
      <div className={`flex items-center justify-center p-4 bg-gray-100 rounded-lg ${className}`}>
        <div className="text-center">
          <MicOff className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Trình duyệt không hỗ trợ ghi âm</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col space-y-3 p-4 bg-white border rounded-lg shadow-sm ${className}`}>
      {/* Recording Status */}
      {recordingState.isRecording && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-red-600">Đang ghi âm</span>
          </div>
          <span className="text-sm text-gray-600">
            {formatDuration(recordingState.recordingDuration)}
          </span>
        </div>
      )}

      {/* Audio Level Visualizer */}
      {recordingState.isRecording && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-100"
            style={{ width: `${getAudioLevelPercentage()}%` }}
          ></div>
        </div>
      )}

      {/* Processing Status */}
      {recordingState.isProcessing && (
        <div className="flex items-center justify-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          <span className="text-sm text-gray-600">Đang xử lý...</span>
        </div>
      )}

      {/* Error Display */}
      {recordingState.error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          {recordingState.error}
        </div>
      )}

      {/* Recording Controls */}
      <div className="flex items-center justify-center space-x-3">
        {!recordingState.isRecording && !audioBlob && (
          <button
            onClick={handleStartRecording}
            disabled={disabled || recordingState.isProcessing || !recordingState.hasPermission}
            className="flex items-center justify-center w-12 h-12 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-full transition-colors"
            title="Bắt đầu ghi âm"
          >
            <Mic className="w-6 h-6" />
          </button>
        )}

        {recordingState.isRecording && (
          <button
            onClick={handleStopRecording}
            disabled={disabled}
            className="flex items-center justify-center w-12 h-12 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white rounded-full transition-colors"
            title="Dừng ghi âm"
          >
            <Square className="w-6 h-6" />
          </button>
        )}

        {audioBlob && !recordingState.isRecording && (
          <>
            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPause}
              disabled={disabled || recordingState.isProcessing}
              className="flex items-center justify-center w-10 h-10 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white rounded-full transition-colors"
              title={isPlaying ? "Tạm dừng" : "Phát"}
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>

            {/* Delete Button */}
            <button
              onClick={handleCancelRecording}
              disabled={disabled || recordingState.isProcessing}
              className="flex items-center justify-center w-10 h-10 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white rounded-full transition-colors"
              title="Xóa bản ghi"
            >
              <Trash2 className="w-5 h-5" />
            </button>

            {/* Send Button */}
            <button
              onClick={handleSendRecording}
              disabled={disabled || recordingState.isProcessing}
              className="flex items-center justify-center w-10 h-10 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-full transition-colors"
              title="Gửi tin nhắn thoại"
            >
              <Send className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Recording Info */}
      {audioBlob && !recordingState.isRecording && (
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Thời lượng: {formatDuration(recordingState.recordingDuration)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Nhấn phát để nghe lại hoặc gửi để chuyển đổi thành văn bản
          </p>
        </div>
      )}

      {/* Permission Request */}
      {!recordingState.hasPermission && recordingState.isSupported && (
        <div className="text-center p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800 mb-2">
            Cần quyền truy cập microphone để ghi âm
          </p>
          <button
            onClick={handleRequestPermission}
            className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-sm rounded transition-colors"
          >
            Thử lại
          </button>
        </div>
      )}
    </div>
  );
};
